import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, RefreshCw, CircleDot } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getH1 } from '../seo/seoRoutes.mjs';
import { translations, DEFAULT_LANGUAGE } from '../utils/languages';

const GeneralSpreadPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [selectedCardCount, setSelectedCardCount] = useState(null);
  const [question, setQuestion] = useState('');
  const [currentQuote, setCurrentQuote] = useState('');



  // Card count options with their meanings
  const cardOptions = [
    { count: 1, name: t('general.singleCard'), description: t('general.singleCardDesc') },
    { count: 3, name: t('general.pastPresentFuture'), description: t('general.pastPresentFutureDesc') },
    { count: 5, name: t('general.fiveCardCross'), description: t('general.fiveCardCrossDesc') },
    { count: 6, name: t('general.hexagramSpread'), description: t('general.hexagramSpreadDesc') },
    { count: 7, name: t('general.chakraAlignment'), description: t('general.chakraAlignmentDesc') },
    { count: 10, name: t('general.celticCross'), description: t('general.celticCrossDesc') },
    { count: 12, name: t('general.yearAhead'), description: t('general.yearAheadDesc') }
  ];

  // Get spread interpretations based on card count using translation keys
  const getSpreadInterpretations = (count) => {
    const positions = [];
    
    // For each position in the spread, get the translated name
    for (let i = 1; i <= count; i++) {
      const translationKey = `general.spread.${count}.position${i}`;
      const translation = translations[currentLanguage]?.[translationKey] || 
                         translations[DEFAULT_LANGUAGE]?.[translationKey] ||
                         `Card ${i}`;
      positions.push(translation);
    }
    
    return positions;
  };

  useEffect(() => {
    // Set a random quote when component mounts
    const wisdomQuotes = t('quotes.general');
    const randomQuote = wisdomQuotes[Math.floor(Math.random() * wisdomQuotes.length)];
    setCurrentQuote(randomQuote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const startGeneralReading = () => {
    if (!selectedCardCount) return;
    
    // Navigate to SpreadDrawingPage with general spread configuration
    navigate('/spread-drawing', {
      state: {
        spreadName: `${t('general.generalReading')} - ${cardOptions.find(o => o.count === selectedCardCount)?.name}`,
        cardCount: selectedCardCount,
        spreadType: "general",
        spreadInterpretations: getSpreadInterpretations(selectedCardCount),
        question: question,
        tarotCards: [], // Use default cards from SpreadDrawingPage
        returnPath: "/general-spread",
        spreadConfig: {
          isGeneralReading: true,
          userQuestion: question
        }
      }
    });
  };

  // Auto-scroll to questions section when card count is selected
  useEffect(() => {
    if (selectedCardCount) {
      const questionSection = document.querySelector('.question-section');
      if (questionSection) {
        questionSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedCardCount]);

  const resetSpread = () => {
    setSelectedCardCount(null);
    
    // Set new random quote and use it as default question
    const wisdomQuotes = t('quotes.general');
    const randomQuote = wisdomQuotes[Math.floor(Math.random() * wisdomQuotes.length)];
    setCurrentQuote(randomQuote);
    setQuestion(randomQuote);
  };

  return (
    <div className="love-spread-page general-spread-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-subtitle">
              {getH1('/general-spread', currentLanguage)}
            </h1>
            <blockquote className="inspiration-question">
              {currentQuote}
            </blockquote>
            <button 
              className="draw-cards-btn"
              onClick={() => {
                navigate('/spread-drawing', { 
                  state: { 
                    spreadName: t('general.generalGuidanceCard'),
                    spreadType: 'single',
                    cardCount: 1,
                    spreadInterpretations: [t('dailyCard.position')],
                    question: currentQuote,
                    returnPath: "/general-spread",
                    spreadConfig: {
                      isSingleCard: true,
                      isDailyCard: true
                    }
                  } 
                })
              }}
            >
              <Sparkles size={20} />
              {t('general.drawGuidanceCard')}
            </button>
          </div>
          <div className="welcome-decoration"></div>
        </div>
      </section>

      <div className="card-selection-section">
        {/* Card Count Selection */}
        <div className="selection-section">
          <h2>1. {t('general.chooseSpreadSize')}</h2>
          <p className="selection-subtitle">{t('general.selectCardCount')}</p>
          
          <div className="card-options-grid">
            {cardOptions.map((option, index) => (
              <div 
                key={index}
                className={`card-option ${selectedCardCount === option.count ? 'selected' : ''}`}
                onClick={() => setSelectedCardCount(option.count)}
              >
                <span className="card-count">{option.count}</span>
                <div className="option-content">
                  <span className="option-name">{option.name}</span>
                  <p className="option-description">{option.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCardCount && (
          <div className="question-section" id="question-section">
            <h2>2. {t('general.focusIntention')}</h2>
            <p className="selection-subtitle">{t('general.centerMindQuestion')}</p>
            
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('general.questionPlaceholder')}
              className="question-input"
              rows="3"
            />
            
            <button 
              onClick={startGeneralReading} 
              className="draw-cards-btn"
            >
              <CircleDot size={20} />
              {t('general.drawCards')}
            </button>

            <button 
              onClick={resetSpread} 
              className="secondary-btn"
              style={{ marginLeft: '1rem' }}
            >
              <RefreshCw size={16} />
              {t('general.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralSpreadPage;